import { Injectable, computed, signal, type Signal, type WritableSignal } from "@angular/core";

import { environment } from "../../../environments/environment";
import { decodeJwtPayload } from "../auth/decode-jwt-payload";
import { CUSTOMER_ADMIN_ROLE, MEMBER_CLAIMS } from "../auth/member-claims";

import type { IAuthRefusal } from "../auth/interfaces";
import type { IPortalUser } from "../models/i-portal-user";

/**
 * The role that grants a Davaco employee cross-organization access — in every
 * spelling it can arrive in.
 *
 * Two of them, and it has to stay that way. An Auth0 role name may contain
 * spaces; an Entra app-role `value` may NOT — Entra refuses to save one, with
 * "Entitlement ClaimValue contains invalid characters". So the same authority
 * reaches this app as "Davaco - Admin" from Auth0 and "Davaco.Admin" from
 * Entra, and checking for only one silently locks out half the people who hold
 * it. customer-portal-api applies the identical list to the verified token.
 */
export const DAVACO_ADMIN_ROLES: readonly string[] = ["Davaco - Admin", "Davaco.Admin"];

function readStrings(claims: Record<string, unknown>, key: string): string[] {
  const value: unknown = claims[key];
  return Array.isArray(value) ? value.filter((v): v is string => typeof v === "string") : [];
}

function readString(claims: Record<string, unknown>, key: string): string {
  const value: unknown = claims[key];
  return typeof value === "string" ? value : "";
}

/**
 * Holds the signed-in identity as signals. Written only by the active
 * IAuthProvider; every other consumer reads.
 *
 * Nothing here is an authorization decision. `isDavacoAdmin` decides which nav
 * items render — customer-portal-api re-checks the same role, from the verified
 * token, on every request it serves.
 */
@Injectable({ providedIn: "root" })
export class AuthStateService {

  private readonly userSignal: WritableSignal<IPortalUser | null> =
    signal<IPortalUser | null>(null);

  public readonly user: Signal<IPortalUser | null> = this.userSignal.asReadonly();

  public readonly isAuthenticated: Signal<boolean> = computed(
    (): boolean => this.userSignal() !== null
  );

  /**
   * What the API says this caller is, once /portal/me has answered.
   *
   * Kept separate from the token claims rather than overwriting them, because
   * the two are different facts: one is what the browser was handed, the other
   * is what the server will actually honour. Only the second decides access.
   */
  private readonly serverRolesSignal: WritableSignal<string[] | null> =
    signal<string[] | null>(null);

  public readonly roles: Signal<string[]> = computed((): string[] => [
    ...(this.userSignal()?.roles ?? []),
    ...(this.serverRolesSignal() ?? []),
  ]);

  public readonly isDavacoAdmin: Signal<boolean> = computed((): boolean =>
    this.roles()
.some((role: string): boolean => DAVACO_ADMIN_ROLES.includes(role))
  );

  /**
   * A customer admin of the organization they signed in to: may manage its
   * members. The API re-checks the same role on every Members request.
   */
  public readonly isCustomerAdmin: Signal<boolean> = computed((): boolean =>
    this.roles().includes(CUSTOMER_ADMIN_ROLE)
  );

  /**
   * Replaced-password override. The claim is only re-minted at the next token
   * refresh, so after a successful change this is set to stop the shell
   * sending the user straight back to /set-password.
   */
  private readonly passwordChangedSignal: WritableSignal<boolean> = signal<boolean>(false);

  /** True while the signed-in customer must replace a temporary password. */
  public readonly mustChangePassword: Signal<boolean> = computed(
    (): boolean => (this.userSignal()?.mustChangePassword ?? false) && !this.passwordChangedSignal()
  );

  public markPasswordChanged(): void {
    this.passwordChangedSignal.set(true);
  }

  /**
   * The API's OWN verdict, kept separate from the union above. Null until
   * /portal/me has answered, and null again after `clear()`.
   *
   * The union is the right basis for rendering — a nav item may appear on
   * either authority. It is the wrong basis for the ENTRY decision, because the
   * two authorities are not interchangeable: the id token's roles come from the
   * SPA registration and the API's from the API registration, and only the
   * latter decides whether a data request is answered. An employee assigned on
   * the SPA app alone unions to `true`, enters the shell, and then gets 403 on
   * everything — the exact state staffAccessGuard exists to prevent.
   */
  private readonly serverIsDavacoAdminSignal: WritableSignal<boolean | null> =
    signal<boolean | null>(null);

  public readonly serverIsDavacoAdmin: Signal<boolean | null> =
    this.serverIsDavacoAdminSignal.asReadonly();

  /**
   * Adopt the API's verdict. Called once at bootstrap from /portal/me.
   *
   * `isDavacoAdmin` is taken as given rather than re-derived from the role
   * strings: the API already applied the accepted-spellings list to a verified
   * token, and re-deciding it here would be a second, driftable copy of the
   * same rule.
   */
  public applyServerIdentity(roles: readonly string[], isDavacoAdmin: boolean): void {
    const alreadySaysAdmin: boolean = roles.some((role: string): boolean =>
      DAVACO_ADMIN_ROLES.includes(role)
    );

    // Only synthesise the canonical spelling when the server says admin but
    // none of the accepted spellings is actually in the list. Appending it
    // unconditionally duplicated the role the user really holds and made
    // Settings display a role list that was not theirs.
    this.serverRolesSignal.set(
      isDavacoAdmin && !alreadySaysAdmin
        ? [...roles, DAVACO_ADMIN_ROLES[0] as string]
        : [...roles]
    );

    this.serverIsDavacoAdminSignal.set(isDavacoAdmin);
  }

  /** Populate from an id token. A token we cannot read clears the session. */
  public setFromIdToken(idToken: string): void {
    const claims: Record<string, unknown> | null = decodeJwtPayload(idToken);
    if (claims === null) {
      this.clear();
      return;
    }

    this.setFromClaims(claims);
  }

  public setFromClaims(claims: Record<string, unknown>): void {
    const organizationId: string = readString(claims, "org_id");
    const organizationName: string = readString(claims, "org_name");

    this.userSignal.set({
      sub: readString(claims, "sub"),
      name: readString(claims, "name"),
      email: readString(claims, "email") || readString(claims, "preferred_username"),
      // Auth0 and Entra both emit roles under the namespaced claim; Entra's
      // app-role claim is the plain `roles` array, so accept either.
      roles: [
        ...readStrings(claims, environment.auth0.rolesClaim),
        ...readStrings(claims, MEMBER_CLAIMS.orgRoles),
        ...readStrings(claims, "roles"),
      ],
      organizationId: organizationId.length > 0 ? organizationId : null,
      organizationName: organizationName.length > 0 ? organizationName : null,
      mustChangePassword: claims[MEMBER_CLAIMS.mustChangePassword] === true,
    });
  }

  /**
   * Auth0 refused the last login, or null. Set once at bootstrap from the
   * callback URL and read by the guard and the login screen, so a refused
   * visitor lands on a screen that says why instead of being sent back to
   * Auth0 to be refused again. Deliberately NOT reset by `clear()`: the
   * provider clears the session and records the refusal in the same pass.
   */
  private readonly refusalSignal: WritableSignal<IAuthRefusal | null> =
    signal<IAuthRefusal | null>(null);

  public readonly refusal: Signal<IAuthRefusal | null> = this.refusalSignal.asReadonly();

  public setRefusal(refusal: IAuthRefusal | null): void {
    this.refusalSignal.set(refusal);
  }

  public clear(): void {
    this.userSignal.set(null);
    this.passwordChangedSignal.set(false);
    this.serverRolesSignal.set(null);
    this.serverIsDavacoAdminSignal.set(null);
  }
}
